import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getContext } from '../utils/tenantBoot';

/**
 * Get current catalog_id for basket scoping.
 * Delegates to tenantBoot's single resolved context (set by main.jsx's
 * boot() before the app renders) - this is the SAME authoritative source
 * used for org_id/store_id everywhere else in the app. The mode
 * ('kiosk'/'website') is folded into the key so Kiosk and Website baskets
 * can never collide even when both resolve to no catalog (e.g. dev
 * fallback, or a screen/domain with no catalog assigned).
 *
 * Previously this read mode-specific localStorage keys directly
 * ('rcc_catalog_kiosk_id' / 'rcc_catalog_website_id'), but nothing ever
 * wrote 'rcc_catalog_kiosk_id', so Kiosk always resolved to null here and
 * fell into the same 'default' bucket Website falls into whenever its own
 * domain resolution also comes back null - letting Kiosk-added items leak
 * into the Website basket (and vice versa).
 * Exported so components can derive catalog-filtered items outside Zustand selectors (stable snapshot fix)
 */
export const getCurrentCatalogId = () => {
  const context = getContext();
  const mode = context?.mode || 'unknown';
  const catalogId = context?.catalogId || 'default';
  // Always mode-qualified (never a bare 'default') so a Kiosk screen and a
  // Website domain that both have no catalog assigned can never share a bucket.
  return `${mode}:${catalogId}`;
};

/**
 * Basket store for managing cart items
 * Persists to localStorage
 * Items are scoped by catalog_id to prevent cross-catalog contamination
 */
export const useBasketStore = create(
  persist(
    (set, get) => ({
      items: [],
      
      // Get items for current catalog only
      getItems: () => {
        const catalogId = getCurrentCatalogId();
        const allItems = get().items;
        if (!catalogId) {
          // No catalog context = show items from 'default' catalog only
          return allItems.filter(item => item.catalog_id === 'default');
        }
        // Filter by catalog when one is active
        return allItems.filter(item => item.catalog_id === catalogId);
      },
      
      // Clear items from other catalogs (run on app init)
      clearOtherCatalogs: () => {
        const catalogId = getCurrentCatalogId();
        if (!catalogId) return; // Don't clear if no catalog context
        
        const allItems = get().items;
        const thisCatalogItems = allItems.filter(item => item.catalog_id === catalogId);
        
        if (thisCatalogItems.length !== allItems.length) {
          console.log('[basketStore] Cleared items from other catalogs. Items before:', allItems.length, 'after:', thisCatalogItems.length);
          set({ items: thisCatalogItems });
        }
      },
      
      // Add item to basket
      addItem: (product, quantity = 1) => {
        const catalogId = getCurrentCatalogId();
        const { items } = get();
        
        // Create unique item identifier including ALL attribute selections
        const selectedAttrs = product.selectedAttributes || [];
        const attrPart = selectedAttrs.length > 0
          ? selectedAttrs.map(attr => `${attr.code}_${attr.value}`).join('_')
          : '';
        const itemId = attrPart 
          ? `${product.barcode}_${attrPart}`
          : product.barcode;
        
        // Use product_name as-is (already includes attributes from ProductDetailPanel)
        const displayName = product.product_name || product.name;
        
        // Tag with catalog_id or 'default' if no catalog
        const itemCatalogId = catalogId || 'default';
        
        const existingIndex = items.findIndex(item => 
          item.itemId === itemId && item.catalog_id === itemCatalogId
        );
        
        if (existingIndex >= 0) {
          // Update quantity if item exists
          const newItems = [...items];
          newItems[existingIndex].qty += quantity;
          set({ items: newItems });
        } else {
          // Add new item with promotion metadata AND catalog_id
          set({
            items: [
              ...items,
              {
                itemId: itemId,
                barcode: product.barcode,
                sku_id: product.sku_id,
                product_name: displayName,
                price: product.price,
                qty: quantity,
                image_url: product.image_url,
                selectedAttributes: selectedAttrs,
                promotion: product.promotion || null, // Store full promotion metadata
                catalog_id: itemCatalogId, // Tag with catalog or 'default'
              },
            ],
          });
        }
      },
      
      // Update item quantity
      updateQuantity: (itemId, quantity) => {
        const catalogId = getCurrentCatalogId() || 'default';
        const { items } = get();
        if (quantity <= 0) {
          // Remove item only if it matches catalog_id
          set({ items: items.filter(item => 
            !(item.itemId === itemId && item.catalog_id === catalogId)
          ) });
        } else {
          const newItems = items.map(item =>
            (item.itemId === itemId && item.catalog_id === catalogId)
              ? { ...item, qty: quantity }
              : item
          );
          set({ items: newItems });
        }
      },
      
      // Remove item from basket
      removeItem: (itemId) => {
        const catalogId = getCurrentCatalogId() || 'default';
        set({ items: get().items.filter(item => 
          !(item.itemId === itemId && item.catalog_id === catalogId)
        ) });
      },
      
      // Clear entire basket (current catalog only)
      clearBasket: () => {
        const catalogId = getCurrentCatalogId() || 'default';
        // Only clear items from current catalog
        const { items } = get();
        set({ items: items.filter(item => item.catalog_id !== catalogId) });
      },
      
      // Get total item count (current catalog only)
      getTotalItems: () => {
        const catalogId = getCurrentCatalogId() || 'default';
        const items = get().items.filter(item => item.catalog_id === catalogId);
        return items.reduce((sum, item) => sum + item.qty, 0);
      },
      
      // Get subtotal (before discounts, current catalog only)
      getSubtotal: () => {
        const catalogId = getCurrentCatalogId() || 'default';
        const items = get().items.filter(item => item.catalog_id === catalogId);
        return items.reduce((sum, item) => sum + (item.price * item.qty), 0);
      },
      
      // Calculate promotion discounts (current catalog only)
      getPromotionDiscounts: () => {
        const catalogId = getCurrentCatalogId() || 'default';
        const allItems = get().items;
        const items = allItems.filter(item => item.catalog_id === catalogId);
        const discounts = [];
        
        console.log('[basketStore] getPromotionDiscounts called with items:', items);
        
        // Group items by promotion ID
        const promotionGroups = {};
        items.forEach(item => {
          // Handle both old (string) and new (object) promotion formats
          if (item.promotion) {
            console.log('[basketStore] Item has promotion:', item.product_name, item.promotion);
            
            // Skip old string format promotions - they can't be calculated
            if (typeof item.promotion === 'string') {
              console.warn('[Basket] Skipping old string-format promotion:', item.promotion);
              return;
            }
            
            // New object format with metadata
            if (item.promotion.promo_id) {
              const promoId = item.promotion.promo_id;
              console.log('[basketStore] Grouping item under promo:', promoId);
              if (!promotionGroups[promoId]) {
                promotionGroups[promoId] = {
                  promo: item.promotion,
                  items: []
                };
              }
              promotionGroups[promoId].items.push(item);
            }
          }
        });
        
        console.log('[basketStore] Promotion groups:', promotionGroups);
        
        // Calculate discount for each promotion
        Object.entries(promotionGroups).forEach(([promoId, group]) => {
          const { promo, items: promoItems } = group;
          const totalQty = promoItems.reduce((sum, item) => sum + item.qty, 0);
          let minQty = promo.min_qty || 1;
          
          // WORKAROUND: Extract min_qty from promotion text if it's wrong
          // "3 for £10" should have min_qty = 3, not 1
          if (promo.text && promo.reward_type === 'PRICE_OVERRIDE') {
            const match = promo.text.match(/(\d+)\s+for/i);
            if (match && match[1]) {
              const extractedQty = parseInt(match[1]);
              if (extractedQty > minQty) {
                console.log(`[basketStore] Correcting min_qty from ${minQty} to ${extractedQty} based on text: "${promo.text}"`);
                minQty = extractedQty;
              }
            }
          }
          
          // Check if this is a multi-group meal deal (items have different group_ids)
          const groupIds = new Set(promoItems.map(item => item.promotion.group_id).filter(Boolean));
          const isMealDeal = groupIds.size > 1;
          
          console.log('[basketStore] Processing promo:', promoId, {
            totalQty,
            minQty,
            reward_type: promo.reward_type,
            reward_value: promo.reward_value,
            isMealDeal,
            uniqueGroups: groupIds.size
          });
          
          let completeSets = 0;
          
          if (isMealDeal) {
            // MEAL DEAL: Need 1 item from each group
            // Group items by group_id and count quantities per group
            const itemsByGroup = {};
            promoItems.forEach(item => {
              const gid = item.promotion.group_id;
              if (gid) {
                if (!itemsByGroup[gid]) {
                  itemsByGroup[gid] = 0;
                }
                itemsByGroup[gid] += item.qty;
              }
            });
            
            // Complete sets = minimum quantity across all groups
            // E.g., 2 sandwiches + 1 drink + 3 snacks = 1 complete meal deal
            const quantitiesPerGroup = Object.values(itemsByGroup);
            if (quantitiesPerGroup.length === groupIds.size) {
              completeSets = Math.min(...quantitiesPerGroup);
              console.log('[basketStore] Meal deal validation:', {
                itemsByGroup,
                completeSets
              });
            } else {
              console.log('[basketStore] Meal deal incomplete - missing items from some groups');
            }
          } else {
            // SIMPLE PROMOTION: Count total items
            if (totalQty >= minQty) {
              completeSets = Math.floor(totalQty / minQty);
            }
          }
          
          if (completeSets > 0) {
            
            if (promo.reward_type === 'MONEY_OFF' || promo.reward_type === 'PRICE_OVERRIDE') {
              // Fixed price promotion (e.g., "3 for £10" or "Meal Deal £3")
              // PRICE_OVERRIDE: Fixed price for a set (reward_value = final price)
              // MONEY_OFF: Discount amount (reward_value = discount)
              
              // Calculate regular price for qualifying items
              let regularPriceForSet;
              
              if (isMealDeal) {
                // For meal deals, calculate price of 1 item from each group
                const groupItemPrices = {};
                promoItems.forEach(item => {
                  const gid = item.promotion.group_id;
                  if (gid && !groupItemPrices[gid]) {
                    // Use cheapest item from each group for the deal
                    groupItemPrices[gid] = item.price;
                  } else if (gid && item.price < groupItemPrices[gid]) {
                    groupItemPrices[gid] = item.price;
                  }
                });
                regularPriceForSet = Object.values(groupItemPrices).reduce((sum, price) => sum + price, 0);
              } else {
                // Simple promotion: average price of items in the set
                regularPriceForSet = promoItems.reduce((sum, item) => {
                  const itemsInSet = Math.min(item.qty, completeSets * minQty);
                  return sum + (item.price * itemsInSet);
                }, 0) / completeSets;
              }
              
              let discountPerSet;
              if (promo.reward_type === 'PRICE_OVERRIDE') {
                // reward_value is the FINAL PRICE for the set (e.g., £10 for 3 items)
                discountPerSet = regularPriceForSet - promo.reward_value;
              } else {
                // MONEY_OFF: reward_value is the DISCOUNT AMOUNT
                discountPerSet = promo.reward_value;
              }
              
              const totalDiscount = discountPerSet * completeSets;
              
              console.log(`[basketStore] ${promo.reward_type} calculation:`, {
                isMealDeal,
                regularPriceForSet,
                discountPerSet,
                totalDiscount,
                completeSets,
                minQty,
                totalQty
              });
              
              if (totalDiscount > 0) {
                discounts.push({
                  promoId,
                  name: promo.text || promo.promo_name,
                  amount: totalDiscount,
                  sets: completeSets
                });
              }
            } else if (promo.reward_type === 'PERCENT_OFF') {
              // Percentage discount
              const qualifyingTotal = promoItems.reduce((sum, item) => {
                const qualifyingQty = Math.min(item.qty, completeSets * minQty);
                return sum + (item.price * qualifyingQty);
              }, 0);
              
              const totalDiscount = qualifyingTotal * (promo.reward_value / 100);
              
              if (totalDiscount > 0) {
                discounts.push({
                  promoId,
                  name: promo.text || promo.promo_name,
                  amount: totalDiscount,
                  sets: completeSets
                });
              }
            }
          }
        });
        
        return discounts;
      },
      
      // Get total discount amount
      getTotalDiscount: () => {
        const discounts = get().getPromotionDiscounts();
        return discounts.reduce((sum, discount) => sum + discount.amount, 0);
      },
      
      // Get final total after discounts
      getTotal: () => {
        return get().getSubtotal() - get().getTotalDiscount();
      },
      
      // Convert to backend format
      toBackendFormat: () => {
        const catalogId = getCurrentCatalogId() || 'default';
        const allItems = get().items;
        const items = allItems.filter(item => item.catalog_id === catalogId);
        return items.map(item => ({
          // Use itemId (which includes variant attributes) as barcode for unique identification
          // This ensures different variants don't get combined in EPOS cart
          barcode: item.itemId,  // Changed from item.barcode to item.itemId
          qty: item.qty,
          name: item.product_name,  // Include the full display name with variant details
        }));
      },
    }),
    {
      name: 'rcc-catalog-basket',
    }
  )
);
